// Libraries
import { SystemStyleObject } from '@chakra-ui/react';
import { ChakraStylesConfig, GroupBase, OptionProps, ValueContainerProps } from 'chakra-react-select';
import { ReactElement } from 'react';
// Types
import { BasicOption } from './FixedDropdown.type';

/**
 * Returns the styles for the value container of a Select input.
 *
 * @function
 * @param {SystemStyleObject} provided - The provided styles for the value container.
 * @param {ValueContainerProps} state - The state of the value container.
 * @returns {SystemStyleObject} The styles for the value container.
 * @template {BasicOption} OptionType - The type of the options in the Select input.
 * @template {boolean} IsMulti - A boolean indicating whether the Select input allows multiple selections.
 */
const valueContainer = <OptionType extends BasicOption, IsMulti extends boolean>(
  provided: SystemStyleObject,
  state: ValueContainerProps<OptionType, IsMulti, GroupBase<OptionType>>
) => {
  const inputValue = ((state.children as ReactElement[])?.[1]?.props as HTMLInputElement)?.value ?? '';
  const placeholder = state.selectProps.placeholder;
  return {
    ...provided,
    border: 'none',
    h: 'full',
    p: 0,
    position: 'relative',
    _after: !inputValue && {
      content: `"${placeholder}"`,
      position: 'absolute',
      color: 'grey.200',
      ml: 3,
      background: `transparent url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='18' height='24' viewBox='0 0 18 18'%3E%3Cpath d='M13.5233 12.4628L16.7355 15.6743L15.6743 16.7355L12.4628 13.5233C11.2678 14.4812 9.7815 15.0022 8.25 15C4.524 15 1.5 11.976 1.5 8.25C1.5 4.524 4.524 1.5 8.25 1.5C11.976 1.5 15 4.524 15 8.25C15.0022 9.7815 14.4812 11.2678 13.5233 12.4628ZM12.0188 11.9063C12.9706 10.9274 13.5022 9.61532 13.5 8.25C13.5 5.349 11.1503 3 8.25 3C5.349 3 3 5.349 3 8.25C3 11.1503 5.349 13.5 8.25 13.5C9.61532 13.5022 10.9274 12.9706 11.9063 12.0188L12.0188 11.9063z' fill='%238484A0' %3E%3C/path%3E%3C/svg%3E") no-repeat`,
      pl: 6,
    },
  };
};

/**
 * Styling function for customizing the appearance of an option in a Select component.
 *
 * @function
 * @param {SystemStyleObject} provided - The provided style object.
 * @param {OptionProps} state - The state object for the option.
 * @returns {SystemStyleObject} The updated style object for the option.
 * @template {BasicOption} OptionType - The type of the options in the Select input.
 * @template {boolean} IsMulti - A boolean indicating whether the Select input allows multiple selections.
 */
const option = <OptionType, IsMulti extends boolean>(
  provided: SystemStyleObject,
  state: OptionProps<OptionType, IsMulti, GroupBase<OptionType>>
) => {
  const bg = state.isFocused ? 'gray.100' : 'transparent';
  return {
    ...provided,
    bg,
    color: 'darkNavy',
    h: 9,
    _active: {
      bg: 'gray.200',
    },
  };
};

/**
 * Styling function for customizing the appearance of the menu in a Select component.
 *
 * @function
 * @param {SystemStyleObject} provided - The provided style object.
 * @returns {SystemStyleObject} The updated style object for the menu.
 */
const menu = (provided: SystemStyleObject) => ({
  ...provided,
  border: 'none',
  mt: 3,
});

/**
 * Styling function for customizing the appearance of the menu list in a Select component.
 *
 * @function
 * @param {SystemStyleObject} provided - The provided style object.
 * @returns {SystemStyleObject} The updated style object for the menu list.
 */
const menuList = (provided: SystemStyleObject) => ({
  ...provided,
  boxShadow: 'none',
  border: 'none',
  borderRadius: '0',
  bg: 'transparent',
  p: 0,
  maxH: 51,
});

/**
 * Styling function for customizing the appearance of the control in a Select component.
 *
 * @function
 * @param {SystemStyleObject} provided - The provided style object.
 * @returns {SystemStyleObject} The updated style object for the control.
 */
const control = (provided: SystemStyleObject) => ({
  ...provided,
  ml: 3,
  w: 'calc(100% - 24px)',
  h: 8,
  minH: 8,
});

/**
 * Styling function for customizing the appearance of the input container in a Select component.
 *
 * @function
 * @param {SystemStyleObject} provided - The provided style object.
 * @returns {SystemStyleObject} The updated style object for the input container.
 */
const inputContainer = (provided: SystemStyleObject) => ({
  ...provided,
  p: 0,
  ml: 3,
});

/**
 * Styling function for customizing the appearance of the multi value in a Select component.
 *
 * @function
 * @param {SystemStyleObject} provided - The provided style object.
 * @returns {SystemStyleObject} The updated style object for the multi value.
 */
const multiValue = () => ({
  display: 'none',
});

/**
 * Returns a styles configuration object for customizing the appearance of a Select component.
 *
 * @function
 * @returns {ChakraStylesConfig} The styles configuration object.
 * @template {BasicOption} OptionType - The type of the options in the Select component.
 * @template {boolean} IsMulti - A boolean value indicating if the Select component allows multiple selections.
 */
export const selectStyles = <OptionType extends BasicOption, IsMulti extends boolean>(): ChakraStylesConfig<
  OptionType,
  IsMulti,
  GroupBase<OptionType>
> => ({
  valueContainer,
  option,
  menu,
  menuList,
  control,
  inputContainer,
  multiValue,
});
